import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import confetti from 'canvas-confetti';
import {
  Eye,
  EyeOff,
  ShieldCheck,
  ShieldAlert,
  ArrowRight,
  ArrowLeft,
  Lock,
  Mail,
  Heart,
  HelpCircle,
  Loader2,
  X,
  Check,
  KeyRound,
  Sparkles,
  RotateCcw,
  Hash,
} from 'lucide-react';
import { CoupleSettings } from '../../types';
import { romanticAudio } from '../../utils/audio';
import {
  signInAdmin,
  registerFirstAdmin,
  bootstrapFirstAdmin,
  requestPasswordResetCode,
  verifyPasswordResetCode,
  submitPasswordResetWithToken,
  detectProfileFromEmail,
  fetchAdminConfigStatus,
} from '../../services/authService';

interface ModernAdminLoginProps {
  settings: CoupleSettings;
  p1Name: string;
  p2Name: string;
  p1Title: string;
  p2Title: string;
  p1Avatar: string;
  p2Avatar: string;
  onNavigateToPublic: (route?: string) => void;
  onSelectProfileAndEnter: (profile: 'novio' | 'novia') => void;
}

export const ModernAdminLogin: React.FC<ModernAdminLoginProps> = ({
  settings,
  p1Name,
  p2Name,
  p1Title,
  p2Title,
  p1Avatar,
  p2Avatar,
  onNavigateToPublic,
  onSelectProfileAndEnter,
}) => {
  // Check if an admin is already registered in project settings
  const hasSettingsAdmin = Boolean(
    settings?.partner1Email ||
    settings?.partner2Email ||
    settings?.partner1Uid ||
    settings?.partner2Uid ||
    settings?.hasAdminConfigured
  );

  // Mode: always default to 'login'
  const [mode, setMode] = useState<'login' | 'setup'>('login');

  // Authoritatively default to true to prevent UI flash while securely verifying against Firebase
  const [hasExistingAdmin, setHasExistingAdmin] = useState<boolean>(true);
  const [isVerifyingAdmin, setIsVerifyingAdmin] = useState<boolean>(true);

  // Sync admin presence from authoritative backend and Firebase
  useEffect(() => {
    let isMounted = true;
    setIsVerifyingAdmin(true);
    fetchAdminConfigStatus()
      .then((serverHasAdmin) => {
        if (isMounted) {
          setHasExistingAdmin(serverHasAdmin);
          setIsVerifyingAdmin(false);
          if (serverHasAdmin && mode === 'setup') {
            setMode('login');
          }
        }
      })
      .catch(() => {
        if (isMounted) {
          setHasExistingAdmin(hasSettingsAdmin);
          setIsVerifyingAdmin(false);
        }
      });
    return () => {
      isMounted = false;
    };
  }, [hasSettingsAdmin, mode]);

  // Input states for form
  const [emailInput, setEmailInput] = useState('');
  const [passwordInput, setPasswordInput] = useState('');
  const [confirmPasswordInput, setConfirmPasswordInput] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [successNotice, setSuccessNotice] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isShaking, setIsShaking] = useState(false);

  // Password Recovery state (Strictly 6-Digit OTP via Hostinger SMTP)
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetStep, setResetStep] = useState<
    'request' | 'verify' | 'new-password' | 'success'
  >('request');
  const [resetEmail, setResetEmail] = useState('');
  const [resetOtpDigits, setResetOtpDigits] = useState<string[]>(['', '', '', '', '', '']);
  const [resetToken, setResetToken] = useState('');
  const [newPasswordInput, setNewPasswordInput] = useState('');
  const [confirmNewPasswordInput, setConfirmNewPasswordInput] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetNotice, setResetNotice] = useState('');
  const [resetError, setResetError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [otpTimer, setOtpTimer] = useState(600); // 10 minutes (600s)
  const otpInputsRef = useRef<(HTMLInputElement | null)[]>([]);

  // Cooldown timer for code re-sending (60 seconds)
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  // 10 minutes OTP countdown timer
  useEffect(() => {
    if (!showResetModal || resetStep !== 'verify' || otpTimer <= 0) return;
    const timer = setInterval(() => {
      setOtpTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [showResetModal, resetStep, otpTimer]);

  // Focus the first OTP box when entering the verification step
  useEffect(() => {
    if (showResetModal && resetStep === 'verify') {
      setTimeout(() => {
        otpInputsRef.current[0]?.focus();
      }, 100);
    }
  }, [showResetModal, resetStep]);

  // Handle mode change with security guard
  const handleSwitchToSetup = () => {
    if (hasExistingAdmin) {
      setLoginError('Ya existe un administrador principal configurado en el sistema.');
      setMode('login');
      return;
    }
    setLoginError('');
    setSuccessNotice('');
    setMode('setup');
  };

  const handleSwitchToLogin = () => {
    setLoginError('');
    setSuccessNotice('');
    setMode('login');
  };

  // Form submission handler: Login or First Admin Setup
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanEmail = emailInput.trim().toLowerCase();
    const cleanPassword = passwordInput.trim();

    // 1. Client-side input validation
    if (!cleanEmail) {
      setLoginError('Ingresa tu correo electrónico.');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(cleanEmail)) {
      setLoginError('El formato de correo electrónico no es válido.');
      return;
    }
    if (!cleanPassword) {
      setLoginError('Ingresa tu contraseña.');
      return;
    }

    if (mode === 'setup') {
      // Guard: strictly block creating second admin
      if (hasExistingAdmin) {
        setIsShaking(true);
        setLoginError('Ya existe un administrador principal configurado en el sistema.');
        setMode('login');
        setTimeout(() => setIsShaking(false), 500);
        return;
      }

      const cleanConfirm = confirmPasswordInput.trim();
      if (cleanPassword.length < 6) {
        setLoginError('La contraseña debe tener al menos 6 caracteres.');
        return;
      }
      if (cleanPassword !== cleanConfirm) {
        setLoginError('Las contraseñas no coinciden.');
        return;
      }
    }

    setIsSubmitting(true);
    setLoginError('');
    setSuccessNotice('');

    try {
      if (mode === 'setup') {
        // Setup flow: create principal admin account & assign Custom Claim via backend
        const result = await bootstrapFirstAdmin(cleanEmail, cleanPassword);

        if (result.success) {
          romanticAudio.playBloomSuccess();
          setHasExistingAdmin(true);
          // Authoritatively verify and enter dashboard immediately
          const loginRes = await signInAdmin(cleanEmail, cleanPassword);
          if (loginRes.success && loginRes.user) {
            const targetProfile =
              loginRes.detectedProfile || detectProfileFromEmail(cleanEmail, settings);
            onSelectProfileAndEnter(targetProfile);
            return;
          }
          setSuccessNotice(
            'Administrador principal configurado exitosamente. Ya puedes iniciar sesión con tus credenciales.'
          );
          setMode('login');
          setPasswordInput('');
          setConfirmPasswordInput('');
        } else {
          setIsShaking(true);
          setLoginError(result.error || 'Se produjo un problema al registrar la cuenta. Inténtalo nuevamente.');
          romanticAudio.playNote(220, 0.2, 'sawtooth', 0.05);
          setTimeout(() => setIsShaking(false), 500);
        }
      } else {
        // Standard admin authentication flow
        const result = await signInAdmin(cleanEmail, cleanPassword);

        if (result.success && result.user) {
          romanticAudio.playChime();
          const targetProfile =
            result.detectedProfile || detectProfileFromEmail(cleanEmail, settings);
          onSelectProfileAndEnter(targetProfile);
        } else {
          setIsShaking(true);
          setLoginError(result.error || 'Correo o contraseña incorrectos.');
          romanticAudio.playNote(220, 0.2, 'sawtooth', 0.05);
          setTimeout(() => setIsShaking(false), 500);
        }
      }
    } catch {
      setIsShaking(true);
      setLoginError('Se produjo un problema al procesar la solicitud. Inténtalo nuevamente.');
      setTimeout(() => setIsShaking(false), 500);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper to format OTP countdown timer (mm:ss)
  const formatOtpTimer = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Helper to mask email for privacy
  const maskEmail = (raw: string) => {
    const [user, domain] = raw.split('@');
    if (!user || !domain) return raw;
    if (user.length <= 2) return `${user[0]}***@${domain}`;
    const visibleChars = user.length > 4 ? 2 : 1;
    const start = user.slice(0, visibleChars);
    const end = user.slice(-visibleChars);
    return `${start}•••${end}@${domain}`;
  };

  // Password Recovery Handlers (Strictly 6-Digit OTP via Hostinger SMTP)
  const handleOpenResetModal = () => {
    setResetEmail(emailInput || '');
    setResetStep('request');
    setResetOtpDigits(['', '', '', '', '', '']);
    setResetToken('');
    setNewPasswordInput('');
    setConfirmNewPasswordInput('');
    setResetNotice('');
    setResetError('');
    setOtpTimer(600);
    setShowResetModal(true);
  };

  const handleCloseResetModal = () => {
    setShowResetModal(false);
    setResetStep('request');
    setResetNotice('');
    setResetError('');
  };

  // Solicitar Código OTP de 6 Dígitos
  const handleRequestCodeSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const cleanEmail = resetEmail.trim().toLowerCase();

    if (!cleanEmail) {
      setResetError('Introduce el correo electrónico asociado a tu cuenta.');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(cleanEmail)) {
      setResetError('El formato de correo electrónico no es válido.');
      return;
    }

    setResetLoading(true);
    setResetError('');
    setResetNotice('');

    try {
      const res = await requestPasswordResetCode(cleanEmail);
      if (res.success) {
        romanticAudio.playBloomSuccess();
        setResetStep('verify');
        setResetOtpDigits(['', '', '', '', '', '']);
        setResendCooldown(60);
        setOtpTimer(600);
        setResetNotice(res.message || 'Hemos enviado un código de 6 dígitos a tu correo.');
      } else {
        setResetError(res.error || 'No pudimos procesar la solicitud. Inténtalo nuevamente.');
      }
    } catch {
      setResetError('No pudimos procesar la solicitud. Inténtalo nuevamente.');
    } finally {
      setResetLoading(false);
    }
  };

  const handleDigitChange = (index: number, val: string) => {
    const cleanChar = val.replace(/\D/g, '').slice(-1);
    const updated = [...resetOtpDigits];
    updated[index] = cleanChar;
    setResetOtpDigits(updated);
    if (resetError) setResetError('');

    if (cleanChar && index < 5) {
      otpInputsRef.current[index + 1]?.focus();
    }
  };

  const handleDigitKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (!resetOtpDigits[index] && index > 0) {
        const updated = [...resetOtpDigits];
        updated[index - 1] = '';
        setResetOtpDigits(updated);
        otpInputsRef.current[index - 1]?.focus();
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      otpInputsRef.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      otpInputsRef.current[index + 1]?.focus();
    }
  };

  const handleDigitPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;

    const updated = [...resetOtpDigits];
    for (let i = 0; i < 6; i++) {
      updated[i] = pasted[i] || '';
    }
    setResetOtpDigits(updated);
    if (resetError) setResetError('');

    const nextEmpty = updated.findIndex((d) => d === '');
    if (nextEmpty !== -1 && nextEmpty < 6) {
      otpInputsRef.current[nextEmpty]?.focus();
    } else {
      otpInputsRef.current[5]?.focus();
    }
  };

  const handleVerifyCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otpTimer <= 0) {
      setResetError('Este código ha expirado.');
      return;
    }
    const cleanEmail = resetEmail.trim().toLowerCase();
    const fullCode = resetOtpDigits.join('');

    if (fullCode.length < 6) {
      setResetError('Introduce los 6 dígitos del código.');
      return;
    }

    setResetLoading(true);
    setResetError('');

    try {
      const res = await verifyPasswordResetCode(cleanEmail, fullCode);
      if (res.success && res.resetToken) {
        romanticAudio.playChime();
        setResetToken(res.resetToken);
        setResetStep('new-password');
        setResetNotice('Código verificado correctamente.');
      } else {
        romanticAudio.playNote(220, 0.2, 'sawtooth', 0.05);
        if (res.attemptsLeft !== undefined) {
          if (res.attemptsLeft <= 0) {
            setResetError('Has superado el número máximo de intentos. Por favor solicita un nuevo código.');
          } else {
            setResetError(`Código incorrecto. Te ${res.attemptsLeft === 1 ? 'queda' : 'quedan'} ${res.attemptsLeft} ${res.attemptsLeft === 1 ? 'intento' : 'intentos'}.`);
          }
        } else {
          setResetError(res.error || 'Código incorrecto. Revisa el código e inténtalo nuevamente.');
        }
      }
    } catch {
      setResetError('No pudimos procesar la solicitud. Inténtalo nuevamente.');
    } finally {
      setResetLoading(false);
    }
  };

  const handleSaveNewPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanPass = newPasswordInput.trim();
    const cleanConfirm = confirmNewPasswordInput.trim();

    if (cleanPass.length < 6) {
      setResetError('La nueva contraseña debe tener al menos 6 caracteres.');
      return;
    }
    if (cleanPass !== cleanConfirm) {
      setResetError('Las contraseñas no coinciden.');
      return;
    }

    setResetLoading(true);
    setResetError('');

    try {
      const res = await submitPasswordResetWithToken(resetToken, cleanPass);
      if (res.success) {
        romanticAudio.playBloomSuccess();
        confetti({
          particleCount: 80,
          spread: 70,
          origin: { y: 0.6 },
          colors: ['#f43f5e', '#fb7185', '#fbbf24', '#f59e0b', '#38bdf8'],
        });
        setResetStep('success');
      } else {
        setResetError(res.error || 'No pudimos procesar la solicitud. Inténtalo nuevamente.');
      }
    } catch {
      setResetError('No pudimos procesar la solicitud. Inténtalo nuevamente.');
    } finally {
      setResetLoading(false);
    }
  };

  const handleFinishSuccess = () => {
    setShowResetModal(false);
    setEmailInput(resetEmail);
    setPasswordInput('');
    setResetStep('request');
    setResetNotice('');
    setResetError('');
  };

  return (
    <div
      id="modern-admin-login"
      className="min-h-screen w-full bg-gradient-to-br from-slate-50 via-rose-50/20 to-amber-50/20 text-slate-800 flex flex-col justify-between items-center p-4 sm:p-6 lg:p-8 select-none relative overflow-x-hidden font-['Plus_Jakarta_Sans',sans-serif]"
    >
      {/* Background subtle romantic ambient illumination */}
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[700px] h-[450px] bg-gradient-to-b from-rose-200/25 via-amber-200/15 to-transparent rounded-full blur-[90px] pointer-events-none" />
      <div className="absolute -bottom-20 -left-20 w-[400px] h-[400px] bg-rose-200/20 rounded-full blur-[80px] pointer-events-none" />
      <div className="absolute -bottom-20 -right-20 w-[400px] h-[400px] bg-amber-200/20 rounded-full blur-[80px] pointer-events-none" />
      <div className="absolute inset-0 bg-[radial-gradient(#e2e8f0_1px,transparent_1px)] [background-size:24px_24px] pointer-events-none opacity-50" />

      {/* Top Header Navigation */}
      <header className="w-full max-w-4xl flex items-center justify-between z-10 pt-2 pb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-tr from-rose-500 to-amber-400 flex items-center justify-center text-white shadow-md shadow-rose-500/20">
            <Heart className="w-5 h-5 text-white fill-white/20" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-['Playfair_Display',serif] font-black text-slate-900 text-base sm:text-lg tracking-tight">
                Nuestros Recuerdos
              </span>
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider uppercase bg-rose-100/90 text-rose-700 border border-rose-200/70">
                Privado
              </span>
            </div>
            <p className="text-[11px] text-slate-500 hidden sm:block">
              {p1Name || 'Novio'} & {p2Name || 'Novia'} • Espacio Exclusivo
            </p>
          </div>
        </div>

        {/* Return to Garden Button */}
        <button
          id="btn-return-to-garden"
          type="button"
          onClick={() => onNavigateToPublic('garden')}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold text-slate-700 hover:text-slate-900 bg-white/90 hover:bg-white border border-slate-200 shadow-xs hover:shadow-sm transition-all cursor-pointer group"
          title="Regresar al Jardín público"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform text-rose-500" />
          <span>Volver al Jardín</span>
        </button>
      </header>

      {/* Central Interactive Card */}
      <main className="w-full max-w-md z-10 my-auto py-6">
        <motion.div
          key={mode}
          initial={{ opacity: 0, scale: 0.97, y: 14 }}
          animate={{
            opacity: 1,
            scale: 1,
            y: 0,
            x: isShaking ? [-8, 8, -6, 6, -3, 3, 0] : 0,
          }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className="w-full bg-white/95 backdrop-blur-xl border border-rose-100/80 rounded-3xl p-7 sm:p-9 shadow-2xl shadow-rose-950/5 relative overflow-hidden"
        >
          {/* Elegant top accent line */}
          <div className="absolute top-0 inset-x-0 h-[3px] bg-gradient-to-r from-rose-500 via-amber-400 to-rose-400" />

          {/* Logo / Heart Emblem */}
          <div className="text-center mb-6">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-tr from-rose-500 via-rose-600 to-amber-400 text-white flex items-center justify-center mx-auto mb-4 shadow-lg shadow-rose-500/25">
              {mode === 'setup' ? (
                <Sparkles className="w-7 h-7 text-white animate-pulse" />
              ) : (
                <Heart className="w-7 h-7 text-white fill-white/20 animate-pulse" />
              )}
            </div>

            <h1 className="font-['Playfair_Display',serif] text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
              {mode === 'setup' ? 'Crear administrador principal' : 'Panel de Administración'}
            </h1>
            <p className="text-xs sm:text-sm text-slate-500 mt-2 leading-relaxed max-w-xs mx-auto">
              {mode === 'setup'
                ? 'Configura la cuenta principal para gestionar este espacio de recuerdos.'
                : 'Gestiona nuestros recuerdos, cartas, música y jardín.'}
            </p>
          </div>

          {/* Notification Messages */}
          <AnimatePresence>
            {successNotice && (
              <motion.div
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                className="mb-4 p-3.5 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs flex items-center gap-2.5 shadow-xs"
              >
                <Check className="w-4 h-4 shrink-0 text-emerald-600" />
                <span className="leading-tight font-medium">{successNotice}</span>
              </motion.div>
            )}

            {loginError && (
              <motion.div
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                className="mb-4 p-3.5 rounded-2xl bg-rose-50 border border-rose-200 text-rose-700 text-xs flex items-center gap-2.5 shadow-xs"
              >
                <ShieldAlert className="w-4 h-4 shrink-0 text-rose-500" />
                <span className="leading-tight font-medium">{loginError}</span>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Campo: Correo Electrónico */}
            <div>
              <label
                htmlFor="admin-login-email-input"
                className="text-[11px] font-bold text-slate-700 tracking-wider uppercase flex items-center gap-1.5 mb-1.5"
              >
                <Mail className="w-3.5 h-3.5 text-rose-500" />
                <span>Correo electrónico</span>
              </label>
              <input
                id="admin-login-email-input"
                type="email"
                value={emailInput}
                onChange={(e) => {
                  setEmailInput(e.target.value);
                  if (loginError) setLoginError('');
                }}
                placeholder="admin@correo.com"
                className="w-full px-4 py-3 bg-slate-50/80 hover:bg-slate-50 focus:bg-white border border-slate-200 hover:border-slate-300 focus:border-rose-400 focus:ring-4 focus:ring-rose-500/10 rounded-2xl text-slate-900 placeholder-slate-400 text-sm font-medium transition-all outline-none"
                required
                autoComplete="email"
              />
            </div>

            {/* Campo: Contraseña con enlace a "¿Olvidaste tu contraseña?" */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label
                  htmlFor="admin-login-password-input"
                  className="text-[11px] font-bold text-slate-700 tracking-wider uppercase flex items-center gap-1.5"
                >
                  <Lock className="w-3.5 h-3.5 text-rose-500" />
                  <span>Contraseña</span>
                </label>

                {mode === 'login' && (
                  <button
                    type="button"
                    onClick={handleOpenResetModal}
                    className="text-[11px] font-semibold text-rose-600 hover:text-rose-700 hover:underline cursor-pointer transition-colors"
                  >
                    ¿Olvidaste tu contraseña?
                  </button>
                )}
              </div>

              <div className="relative">
                <input
                  id="admin-login-password-input"
                  type={showPassword ? 'text' : 'password'}
                  value={passwordInput}
                  onChange={(e) => {
                    setPasswordInput(e.target.value);
                    if (loginError) setLoginError('');
                  }}
                  placeholder="••••••••••••"
                  className="w-full pl-4 pr-11 py-3 bg-slate-50/80 hover:bg-slate-50 focus:bg-white border border-slate-200 hover:border-slate-300 focus:border-rose-400 focus:ring-4 focus:ring-rose-500/10 rounded-2xl text-slate-900 placeholder-slate-400 text-sm font-mono tracking-wider transition-all outline-none"
                  required
                  autoComplete={mode === 'setup' ? 'new-password' : 'current-password'}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-slate-700 transition-colors cursor-pointer rounded-lg focus:outline-hidden focus:ring-2 focus:ring-rose-400"
                  aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  title={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                >
                  {showPassword ? <EyeOff className="w-4 h-4 text-rose-500" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Campo Confirmar Contraseña (solo en creación del primer admin) */}
            {mode === 'setup' && (
              <div>
                <label
                  htmlFor="admin-confirm-password-input"
                  className="text-[11px] font-bold text-slate-700 tracking-wider uppercase flex items-center gap-1.5 mb-1.5"
                >
                  <Lock className="w-3.5 h-3.5 text-rose-500" />
                  <span>Confirmar contraseña</span>
                </label>

                <div className="relative">
                  <input
                    id="admin-confirm-password-input"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPasswordInput}
                    onChange={(e) => {
                      setConfirmPasswordInput(e.target.value);
                      if (loginError) setLoginError('');
                    }}
                    placeholder="••••••••••••"
                    className="w-full pl-4 pr-11 py-3 bg-slate-50/80 hover:bg-slate-50 focus:bg-white border border-slate-200 hover:border-slate-300 focus:border-rose-400 focus:ring-4 focus:ring-rose-500/10 rounded-2xl text-slate-900 placeholder-slate-400 text-sm font-mono tracking-wider transition-all outline-none"
                    required
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-slate-700 transition-colors cursor-pointer rounded-lg focus:outline-hidden focus:ring-2 focus:ring-rose-400"
                    aria-label={showConfirmPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    title={showConfirmPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4 text-rose-500" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            )}

            {/* Botón Principal */}
            <button
              id="btn-admin-submit-auth"
              type="submit"
              disabled={isSubmitting}
              className="w-full py-3.5 px-5 bg-gradient-to-r from-rose-500 via-rose-600 to-amber-500 hover:opacity-95 active:scale-[0.99] text-white font-bold text-sm rounded-2xl shadow-md shadow-rose-500/20 transition-all flex items-center justify-center gap-2 cursor-pointer group disabled:opacity-50 mt-3"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin text-white" />
                  <span>{mode === 'setup' ? 'Creando administrador…' : 'Verificando acceso…'}</span>
                </>
              ) : (
                <>
                  <span>{mode === 'setup' ? 'Crear administrador principal' : 'Iniciar sesión'}</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform text-white" />
                </>
              )}
            </button>
          </form>

          {/* Selector de Primer Administrador (Sólo si Firebase confirmó de forma real que NO existe administrador) */}
          {!isVerifyingAdmin && mode === 'login' && !hasExistingAdmin && (
            <div className="mt-4 pt-3.5 border-t border-slate-100 text-center">
              <button
                type="button"
                onClick={handleSwitchToSetup}
                className="text-xs text-rose-600 hover:text-rose-700 font-semibold transition-colors cursor-pointer hover:underline inline-flex items-center gap-1.5"
              >
                <span>¿Primera configuración? Crear administrador principal</span>
              </button>
            </div>
          )}

          {/* Volver al login desde la creación */}
          {mode === 'setup' && (
            <div className="mt-4 pt-3.5 border-t border-slate-100 text-center">
              <button
                type="button"
                onClick={handleSwitchToLogin}
                className="text-xs text-slate-500 hover:text-slate-800 font-medium transition-colors cursor-pointer hover:underline inline-flex items-center gap-1"
              >
                <ArrowLeft className="w-3.5 h-3.5 text-rose-500" />
                <span>Volver al inicio de sesión</span>
              </button>
            </div>
          )}

          {/* Enlace de Asistencia */}
          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={() => setShowHelpModal(true)}
              className="text-xs text-slate-400 hover:text-slate-700 transition-colors cursor-pointer inline-flex items-center gap-1 font-medium hover:underline"
            >
              <HelpCircle className="w-3.5 h-3.5 text-slate-400" />
              <span>¿Problemas para acceder?</span>
            </button>
          </div>

          {/* Footer de Protección */}
          <div className="mt-5 pt-3.5 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-500">
            <span className="flex items-center gap-1.5 text-emerald-600 font-semibold">
              <ShieldCheck className="w-3.5 h-3.5" />
              <span>Acceso protegido</span>
            </span>
            <span className="text-slate-400 font-medium">Nuestros Recuerdos</span>
          </div>
        </motion.div>
      </main>

      {/* Footer General */}
      <footer className="text-center text-[11px] text-slate-400 py-3 z-10 font-medium">
        <span>Nuestros Recuerdos • Espacio Privado & Seguro de Pareja</span>
      </footer>

      {/* MODAL: Recuperación de Contraseña Profesional por Código de 6 Dígitos */}
      <AnimatePresence>
        {showResetModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/50 backdrop-blur-xs">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              className="w-full max-w-sm sm:max-w-md bg-white rounded-3xl p-6 sm:p-8 shadow-2xl border border-slate-100 relative overflow-hidden"
            >
              {/* Close Button */}
              <button
                type="button"
                onClick={handleCloseResetModal}
                className="absolute top-4 right-4 p-1.5 text-slate-400 hover:text-slate-700 rounded-full hover:bg-slate-100 transition-colors cursor-pointer"
                aria-label="Cerrar modal"
              >
                <X className="w-4 h-4" />
              </button>

              {/* Security notice */}
              <span className="sr-only">
                Recuperación de contraseña mediante código de verificación de 6 dígitos.
              </span>

              {/* ------------------------------------------------------------- */}
              {/* PASO 1: Formulario de Correo (Recuperar contraseña)           */}
              {/* ------------------------------------------------------------- */}
              {resetStep === 'request' && (
                <div>
                  <div className="w-12 h-12 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center mb-3">
                    <KeyRound className="w-6 h-6 text-rose-500" />
                  </div>

                  <h3 className="font-['Playfair_Display',serif] text-xl font-bold text-slate-900 tracking-tight">
                    Recuperar contraseña
                  </h3>
                  <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                    Introduce el correo electrónico asociado a tu cuenta y te enviaremos un código de verificación.
                  </p>

                  {resetNotice && (
                    <div className="mt-4 p-3.5 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs flex items-start gap-2.5">
                      <Check className="w-4 h-4 shrink-0 text-emerald-600 mt-0.5" />
                      <span className="leading-relaxed font-medium">{resetNotice}</span>
                    </div>
                  )}

                  {resetError && (
                    <div className="mt-4 p-3 rounded-2xl bg-rose-50 border border-rose-200 text-rose-700 text-xs flex items-center gap-2">
                      <ShieldAlert className="w-4 h-4 shrink-0 text-rose-500" />
                      <span className="font-medium">{resetError}</span>
                    </div>
                  )}

                  <form onSubmit={handleRequestCodeSubmit} className="mt-5 space-y-4">
                    <div>
                      <label
                        htmlFor="reset-email-field"
                        className="text-[11px] font-bold text-slate-700 tracking-wider uppercase flex items-center gap-1.5 mb-1.5"
                      >
                        <Mail className="w-3.5 h-3.5 text-rose-500" />
                        <span>Correo electrónico</span>
                      </label>
                      <input
                        id="reset-email-field"
                        type="email"
                        value={resetEmail}
                        onChange={(e) => {
                          setResetEmail(e.target.value);
                          if (resetError) setResetError('');
                        }}
                        placeholder="tu-correo@ejemplo.com"
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 focus:bg-white focus:border-rose-400 focus:ring-4 focus:ring-rose-500/10 rounded-2xl text-slate-900 placeholder-slate-400 text-sm font-medium transition-all outline-none"
                        required
                        autoComplete="email"
                        autoFocus
                      />
                    </div>

                    <div className="flex flex-col gap-2 pt-1">
                      <button
                        type="submit"
                        disabled={resetLoading}
                        className="w-full py-3 px-4 bg-gradient-to-r from-rose-500 via-rose-600 to-amber-500 hover:opacity-95 text-white font-bold text-xs rounded-xl shadow-md shadow-rose-500/20 transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                      >
                        {resetLoading ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin text-white" />
                            <span>Enviando código…</span>
                          </>
                        ) : (
                          <span>Enviar código</span>
                        )}
                      </button>

                      <button
                        type="button"
                        onClick={handleCloseResetModal}
                        className="w-full py-2 text-xs text-slate-500 hover:text-slate-800 font-semibold rounded-xl hover:bg-slate-100 transition-colors cursor-pointer text-center"
                      >
                        Volver al inicio de sesión
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* ------------------------------------------------------------- */}
              {/* PASO 2: Verificación de Código OTP (6 Dígitos)               */}
              {/* ------------------------------------------------------------- */}
              {resetStep === 'verify' && (
                <div>
                  <div className="w-12 h-12 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center mb-3">
                    <Mail className="w-6 h-6 text-rose-500" />
                  </div>

                  <h3 className="font-['Playfair_Display',serif] text-xl font-bold text-slate-900 tracking-tight">
                    Verifica tu correo
                  </h3>
                  <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">
                    Enviamos un código de 6 dígitos a tu correo electrónico:<br />
                    <strong className="text-slate-800 font-semibold break-all">{maskEmail(resetEmail)}</strong>
                  </p>

                  {/* Real Countdown Timer Banner */}
                  <div className="mt-3 py-2 px-3 rounded-2xl bg-slate-50 border border-slate-200/80 flex items-center justify-between text-xs">
                    {otpTimer > 0 ? (
                      <span className="text-slate-600 font-medium flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                        <span>Código válido durante <strong>{formatOtpTimer(otpTimer)}</strong></span>
                      </span>
                    ) : (
                      <span className="text-rose-600 font-semibold flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-rose-500" />
                        <span>Este código ha expirado.</span>
                      </span>
                    )}

                    {otpTimer <= 0 && (
                      <button
                        type="button"
                        onClick={() => handleRequestCodeSubmit()}
                        disabled={resetLoading}
                        className="text-[11px] font-bold text-rose-600 hover:text-rose-700 underline cursor-pointer"
                      >
                        Solicitar nuevo código
                      </button>
                    )}
                  </div>

                  {/* Notices */}
                  {resetNotice && (
                    <div className="mt-3 p-3 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs flex items-start gap-2">
                      <Check className="w-4 h-4 shrink-0 text-emerald-600 mt-0.5" />
                      <span className="leading-tight font-medium">{resetNotice}</span>
                    </div>
                  )}

                  {resetError && (
                    <div className="mt-3 p-3 rounded-2xl bg-rose-50 border border-rose-200 text-rose-700 text-xs flex items-center gap-2">
                      <ShieldAlert className="w-4 h-4 shrink-0 text-rose-500" />
                      <span className="font-medium">{resetError}</span>
                    </div>
                  )}

                  <form onSubmit={handleVerifyCodeSubmit} className="mt-4 space-y-4">
                    {/* 6 Casillas Numéricas Independientes */}
                    <div>
                      <label className="text-[11px] font-bold text-slate-700 tracking-wider uppercase block text-center mb-2.5">
                        Código de verificación
                      </label>
                      <div
                        className="flex items-center justify-center gap-2 sm:gap-2.5"
                        onPaste={handleDigitPaste}
                      >
                        {resetOtpDigits.map((digit, idx) => (
                          <input
                            key={idx}
                            ref={(el) => {
                              otpInputsRef.current[idx] = el;
                            }}
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            maxLength={1}
                            value={digit}
                            disabled={otpTimer <= 0 || resetLoading}
                            onChange={(e) => handleDigitChange(idx, e.target.value)}
                            onKeyDown={(e) => handleDigitKeyDown(idx, e)}
                            className="w-10 h-12 sm:w-11 sm:h-13 text-center text-lg sm:text-xl font-mono font-bold text-slate-900 bg-slate-50 hover:bg-slate-100/70 focus:bg-white border-2 border-slate-200 focus:border-rose-500 focus:ring-4 focus:ring-rose-500/10 rounded-xl transition-all outline-none disabled:opacity-50"
                            autoComplete="one-time-code"
                          />
                        ))}
                      </div>
                    </div>

                    <div className="flex flex-col gap-2 pt-2">
                      <button
                        type="submit"
                        disabled={resetLoading || resetOtpDigits.join('').length < 6 || otpTimer <= 0}
                        className="w-full py-3 px-4 bg-gradient-to-r from-rose-500 via-rose-600 to-amber-500 hover:opacity-95 text-white font-bold text-xs rounded-xl shadow-md shadow-rose-500/20 transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-40"
                      >
                        {resetLoading ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin text-white" />
                            <span>Verificando…</span>
                          </>
                        ) : (
                          <span>Verificar código</span>
                        )}
                      </button>

                      {/* Reenvío con protección antispam (60 segundos) */}
                      <div className="text-center pt-1.5 pb-1">
                        {resendCooldown > 0 ? (
                          <span className="text-[11px] text-slate-400 font-medium">
                            Puedes solicitar un nuevo código en {resendCooldown} s
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleRequestCodeSubmit()}
                            disabled={resetLoading}
                            className="text-xs text-rose-600 hover:text-rose-700 font-semibold hover:underline inline-flex items-center gap-1 cursor-pointer transition-colors"
                          >
                            <RotateCcw className="w-3 h-3 text-rose-500" />
                            <span>Reenviar código</span>
                          </button>
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={() => {
                          setResetStep('request');
                          setResetError('');
                          setResetNotice('');
                        }}
                        className="w-full py-2 text-xs text-slate-500 hover:text-slate-800 font-medium rounded-xl hover:bg-slate-100 transition-colors cursor-pointer"
                      >
                        Cambiar correo
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* ------------------------------------------------------------- */}
              {/* PASO 3: Crear Nueva Contraseña                               */}
              {/* ------------------------------------------------------------- */}
              {resetStep === 'new-password' && (
                <div>
                  <div className="w-12 h-12 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center mb-3">
                    <Lock className="w-6 h-6 text-rose-500" />
                  </div>

                  <h3 className="font-['Playfair_Display',serif] text-xl font-bold text-slate-900 tracking-tight">
                    Crear nueva contraseña
                  </h3>
                  <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">
                    Introduce tu nueva contraseña para acceder a Nuestros Recuerdos.
                  </p>

                  {/* Errors */}
                  {resetError && (
                    <div className="mt-4 p-3 rounded-2xl bg-rose-50 border border-rose-200 text-rose-700 text-xs flex items-center gap-2">
                      <ShieldAlert className="w-4 h-4 shrink-0 text-rose-500" />
                      <span className="font-medium">{resetError}</span>
                    </div>
                  )}

                  <form onSubmit={handleSaveNewPasswordSubmit} className="mt-4 space-y-3.5">
                    <div>
                      <label
                        htmlFor="new-password-field"
                        className="text-[11px] font-bold text-slate-700 tracking-wider uppercase flex items-center gap-1.5 mb-1.5"
                      >
                        <Lock className="w-3.5 h-3.5 text-rose-500" />
                        <span>Nueva contraseña</span>
                      </label>
                      <div className="relative">
                        <input
                          id="new-password-field"
                          type={showNewPassword ? 'text' : 'password'}
                          value={newPasswordInput}
                          onChange={(e) => {
                            setNewPasswordInput(e.target.value);
                            if (resetError) setResetError('');
                          }}
                          placeholder="••••••••••••"
                          className="w-full pl-4 pr-11 py-3 bg-slate-50 border border-slate-200 focus:bg-white focus:border-rose-400 focus:ring-4 focus:ring-rose-500/10 rounded-2xl text-slate-900 placeholder-slate-400 text-sm font-mono tracking-wider transition-all outline-none"
                          required
                          autoComplete="new-password"
                          autoFocus
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword((p) => !p)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-slate-700 transition-colors cursor-pointer"
                          aria-label={showNewPassword ? 'Ocultar' : 'Mostrar'}
                        >
                          {showNewPassword ? <EyeOff className="w-4 h-4 text-rose-500" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label
                        htmlFor="confirm-new-password-field"
                        className="text-[11px] font-bold text-slate-700 tracking-wider uppercase flex items-center gap-1.5 mb-1.5"
                      >
                        <Lock className="w-3.5 h-3.5 text-rose-500" />
                        <span>Confirmar nueva contraseña</span>
                      </label>
                      <div className="relative">
                        <input
                          id="confirm-new-password-field"
                          type={showConfirmNewPassword ? 'text' : 'password'}
                          value={confirmNewPasswordInput}
                          onChange={(e) => {
                            setConfirmNewPasswordInput(e.target.value);
                            if (resetError) setResetError('');
                          }}
                          placeholder="••••••••••••"
                          className="w-full pl-4 pr-11 py-3 bg-slate-50 border border-slate-200 focus:bg-white focus:border-rose-400 focus:ring-4 focus:ring-rose-500/10 rounded-2xl text-slate-900 placeholder-slate-400 text-sm font-mono tracking-wider transition-all outline-none"
                          required
                          autoComplete="new-password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmNewPassword((p) => !p)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-slate-700 transition-colors cursor-pointer"
                          aria-label={showConfirmNewPassword ? 'Ocultar' : 'Mostrar'}
                        >
                          {showConfirmNewPassword ? <EyeOff className="w-4 h-4 text-rose-500" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    <div className="text-[11px] text-slate-400 pl-1">
                      Mínimo 6 caracteres. Ambas contraseñas deben coincidir.
                    </div>

                    <div className="flex flex-col gap-2 pt-2">
                      <button
                        type="submit"
                        disabled={resetLoading}
                        className="w-full py-3 px-4 bg-gradient-to-r from-rose-500 via-rose-600 to-amber-500 hover:opacity-95 text-white font-bold text-xs rounded-xl shadow-md shadow-rose-500/20 transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                      >
                        {resetLoading ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin text-white" />
                            <span>Actualizando contraseña…</span>
                          </>
                        ) : (
                          <span>Actualizar contraseña</span>
                        )}
                      </button>

                      <button
                        type="button"
                        onClick={handleCloseResetModal}
                        className="w-full py-2 text-xs text-slate-500 hover:text-slate-800 font-medium rounded-xl hover:bg-slate-100 transition-colors cursor-pointer"
                      >
                        Cancelar
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* ------------------------------------------------------------- */}
              {/* PASO 4: Contraseña Actualizada (Éxito)                         */}
              {/* ------------------------------------------------------------- */}
              {resetStep === 'success' && (
                <div className="text-center py-2">
                  <div className="w-16 h-16 rounded-3xl bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto mb-4 border border-emerald-100 shadow-sm">
                    <Check className="w-8 h-8 text-emerald-600" />
                  </div>

                  <h3 className="font-['Playfair_Display',serif] text-2xl font-bold text-slate-900 tracking-tight">
                    Contraseña actualizada correctamente.
                  </h3>
                  <p className="text-xs text-slate-600 mt-2 leading-relaxed max-w-xs mx-auto">
                    Tu contraseña se ha actualizado correctamente en Firebase Authentication. Ya puedes iniciar sesión con tu nueva contraseña.
                  </p>

                  <div className="mt-6">
                    <button
                      type="button"
                      onClick={handleFinishSuccess}
                      className="w-full py-3.5 px-5 bg-gradient-to-r from-rose-500 via-rose-600 to-amber-500 hover:opacity-95 text-white font-bold text-xs rounded-xl shadow-md shadow-rose-500/20 transition-all flex items-center justify-center gap-2 cursor-pointer"
                    >
                      <span>Volver al inicio de sesión</span>
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* MODAL: ¿Problemas para acceder? */}
      <AnimatePresence>
        {showHelpModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/50 backdrop-blur-xs">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              className="w-full max-w-sm bg-white rounded-3xl p-6 shadow-2xl border border-slate-100 relative"
            >
              <button
                type="button"
                onClick={() => setShowHelpModal(false)}
                className="absolute top-4 right-4 p-1.5 text-slate-400 hover:text-slate-700 rounded-full hover:bg-slate-100 transition-colors cursor-pointer"
                aria-label="Cerrar ventana de ayuda"
              >
                <X className="w-4 h-4" />
              </button>

              <div className="w-10 h-10 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center mb-3">
                <HelpCircle className="w-5 h-5 text-amber-500" />
              </div>

              <h3 className="font-['Playfair_Display',serif] text-lg font-bold text-slate-900 tracking-tight">
                Acceso al Panel Privado
              </h3>

              <div className="mt-2.5 text-xs text-slate-600 space-y-2 leading-relaxed">
                <p>
                  El panel de administración está reservado exclusivamente para los creadores de esta historia de amor.
                </p>
                <p>
                  Si olvidaste tu contraseña, pulsa <strong>¿Olvidaste tu contraseña?</strong> en la pantalla de acceso para recibir un código de verificación de 6 dígitos.
                </p>
                <p className="text-slate-400 text-[11px]">
                  Por motivos de privacidad y seguridad, este panel no expone datos internos ni correos en el navegador.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setShowHelpModal(false)}
                className="mt-5 w-full py-2.5 bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs rounded-xl transition-all cursor-pointer"
              >
                Entendido
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};
